import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, Signal, ViewEncapsulation } from '@angular/core';
import {
  AtIconComponent,
  AtOverlayComponent,
  AtSkeletonComponent,
  ConfigurableAuthService,
} from '@anothertrip/anothertrip-ds';
import { take } from 'rxjs';

import { PaywallService } from '../../paywall/services/paywall.service';
import { User, UserService } from '../../services/user/user.service';
import { UserProfileOverlayComponent } from '../user-profile-overlay/user-profile-overlay.component';

@Component({
  selector: 'app-sidebar-footer',
  standalone: true,
  imports: [CdkOverlayOrigin, AtIconComponent, AtOverlayComponent, AtSkeletonComponent, UserProfileOverlayComponent],
  templateUrl: './sidebar-footer.component.html',
  styleUrl: './sidebar-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class SidebarFooterComponent {
  private readonly userService = inject(UserService);
  private readonly authService = inject(ConfigurableAuthService);
  private readonly paywall = inject(PaywallService);

  protected readonly user: Signal<User | null> = this.userService.currentUser;
  protected readonly isUserLoading = this.userService.isLoading;

  protected readonly username = computed<string | undefined>(() => this.user()?.name);
  protected readonly email = computed<string | undefined>(() => this.user()?.email);

  protected readonly initials = computed<string>(() =>
    (this.username() ?? '?')
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('')
  );

  /**
   * The tier the API grants, NOT the one baked into the ID token.
   *
   * The token's subscription claims are a snapshot taken when Cognito last wrote the user's
   * attributes, and they drift: an account renewed after its recorded expiry keeps minting FREE
   * long after /subscription/status started answering PRO. The paywall — which is what actually
   * unlocks the features — is the only source that agrees with what the operator can do, so the
   * line that names their plan reads from there.
   *
   * Null until it is known — NOT 'FREE'. Defaulting an unknown tier to the lowest one tells the
   * operator they are on a plan they may not be on.
   */
  protected readonly tier = computed<string | null>(() => this.paywall.currentTier());

  /** The account panel, which the trigger's `aria-controls` points at while it is open. */
  protected readonly panelId = 'sidebar-account-panel';
  /** The panel's visually hidden heading, which names the overlay's dialog. */
  protected readonly headingId = 'sidebar-account-heading';

  protected onLogout(): void {
    this.authService.logout(this.user()?.authProvider).pipe(take(1)).subscribe();
  }
}
